import { useI18n } from 'hooks/useI18n';
import { ListItemStringValue } from 'interfaces/ui/ui-list-item';
import { OffersListFilter } from 'model/model-offers-list-filter';
import { useRouter } from 'next/dist/client/router';
import { useCallback, useMemo } from 'react';
import { getTranslatedText } from 'services/multilingual.service';
import { UseOfferListFilters } from './Filters.types';

export const useOfferListFilters: UseOfferListFilters = ({
  currentFilter,
  languages,
  currencies,
  onFilterChange,
  onFilterReset
}) => {
  const { t } = useI18n('job-offer-list');
  const router = useRouter();

  const languageValues = useMemo(() => {
    return [
      {
        label: t('any_language'),
        value: ''
      },
      ...languages.map((l) => ({
        label: getTranslatedText(l.name, router.locale),
        value: l.code
      }))
    ];
  }, [languages, router.locale, t]);

  const currencyValues = useMemo(() => {
    const values = currencies.map(({ symbol, label }) => ({
      label: symbol,
      value: symbol,
      title: label
    }));

    values.splice(2, 0, {
      label: '---',
      value: '',
      title: ''
    });

    return values;
  }, [currencies]);

  const updateFilter = useCallback(
    (newFilter: OffersListFilter): void => {
      onFilterChange(newFilter);
    },
    [onFilterChange]
  );

  const handleResetFiltersClick = useCallback((): void => {
    onFilterReset();
  }, [onFilterReset]);

  const getLabelValueForCurrentFilterLanguages =
    useCallback((): ListItemStringValue => {
      if (currentFilter.offerLanguage) {
        const selectedLanguage = languages.find(
          (language) => language.code === currentFilter.offerLanguage
        );
        return {
          label: getTranslatedText(selectedLanguage?.name, router.locale),
          value: selectedLanguage?.code || ''
        };
      } else {
        return {
          ...languageValues[0],
          label: languageValues[0].label
        };
      }
    }, [currentFilter.offerLanguage, languageValues, languages, router.locale]);

  return {
    t,
    currencyValues,
    languageValues,
    getLabelValueForCurrentFilterLanguages,
    handleResetFiltersClick,
    updateFilter
  };
};
